import { differenceInCalendarDays, differenceInMonths } from 'date-fns';

/**
 * Age milestones for the dashboard celebration.
 *
 * `emoji` is CONTENT (the celebration glyph), not an icon — it is rendered
 * aria-hidden inside MilestoneCelebration, which supplies the spoken label.
 */
const milestones = [
  { days: 1, key: '1day', emoji: '🎉', label: '1 day old!' },
  { days: 7, key: '1week', emoji: '🌟', label: '1 week old!' },
  { days: 14, key: '2weeks', emoji: '✨', label: '2 weeks old!' },
  { days: 30, key: '1month', emoji: '🎂', label: '1 month old!' },
  { days: 100, key: '100days', emoji: '💯', label: '100 days old!' },
  { days: 182, key: '6months', emoji: '🎊', label: '6 months old!' },
  { days: 365, key: '1year', emoji: '🥳', label: '1 year old!' },
  { days: 548, key: '18months', emoji: '🌈', label: '18 months old!' },
  { days: 730, key: '2years', emoji: '🎈', label: '2 years old!' },
];

/**
 * The milestone for TODAY, or null.
 *
 * Exact-day match on CALENDAR days (a ±1-day window used to show "1 day old!"
 * across three separate days). Calendar days rather than elapsed 24 h periods
 * so the celebration flips at local midnight, not at the hour of birth.
 */
export function getCurrentMilestone(dateOfBirth, dismissedCelebrations = []) {
  if (!dateOfBirth) return null;

  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;

  const ageDays = differenceInCalendarDays(new Date(), dob);
  const dismissed = Array.isArray(dismissedCelebrations) ? dismissedCelebrations : [];

  return milestones.find((ms) => ms.days === ageDays && !dismissed.includes(ms.key)) || null;
}

/** "5 days old" / "3 weeks old" / "4 months old" / "1 year, 2 months old". */
export function getBabyAgeText(dateOfBirth) {
  if (!dateOfBirth) return '';
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return '';
  const now = new Date();
  const months = differenceInMonths(now, dob);
  const days = differenceInCalendarDays(now, dob);

  if (days < 0) return '';
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} old`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) !== 1 ? 's' : ''} old`;
  if (months < 24) return `${months} month${months !== 1 ? 's' : ''} old`;
  const years = Math.floor(months / 12);
  const remainMonths = months % 12;
  return remainMonths > 0
    ? `${years} year${years !== 1 ? 's' : ''}, ${remainMonths} month${remainMonths !== 1 ? 's' : ''} old`
    : `${years} year${years !== 1 ? 's' : ''} old`;
}

/**
 * Short age band for hero copy: "3 weeks" / "4 months" / "2 years".
 * Falls back to 'this age' when the date of birth is unknown.
 */
export function getBabyAgeBand(dateOfBirth) {
  if (!dateOfBirth) return 'this age';
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return 'this age';
  const now = new Date();
  const months = differenceInMonths(now, dob);
  const days = differenceInCalendarDays(now, dob);
  if (days < 0) return 'this age';
  if (months < 1) {
    const weeks = Math.max(0, Math.floor(days / 7));
    return `${weeks} week${weeks !== 1 ? 's' : ''}`;
  }
  if (months < 24) return `${months} month${months !== 1 ? 's' : ''}`;
  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''}`;
}
